import React from 'react';
import { Info } from 'lucide-react';

/**
 * Sezione "Contratto e servizi" della scheda azienda: mostra il tipo di
 * contratto normalizzato (S / M / S+M) e i servizi acquistati (DVR, DUVRI,
 * incarichi, sopralluoghi, ecc.).
 *
 * Sola lettura: la modifica passa dall'importer dedicato o dal PATCH
 * standard `/api/companies/{id}`.
 */
const CONTRATTO_LABEL = {
  S: { label: 'Solo Sicurezza', color: 'bg-sky-50 text-sky-700 border-sky-200' },
  M: { label: 'Solo Medicina', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  'S+M': { label: 'Sicurezza + Medicina', color: 'bg-violet-50 text-violet-700 border-violet-200' },
  RSPP: { label: 'Solo RSPP', color: 'bg-teal-50 text-teal-700 border-teal-200' },
  HACCP: { label: 'Solo HACCP', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  AUTOGESTITA: { label: 'Autogestita', color: 'bg-slate-100 text-slate-700 border-slate-300' },
};

const SERVIZI_GROUPS = [
  {
    title: 'Incarichi',
    items: [
      ['incaricoMedico', 'Medico competente'],
      ['incaricoRSPP', 'RSPP'],
      ['supportoRSPP', 'Supporto RSPP'],
      ['supportoHACCP', 'Supporto HACCP'],
      ['incaricoRespPiscina', 'Resp. Piscina'],
      ['supportoPiscina', 'Supporto Piscina'],
    ],
  },
  {
    title: 'DVR e valutazioni specifiche',
    items: [
      ['dvrBase', 'DVR base'],
      ['dvrStress', 'DVR Stress'],
      ['dvrMadri', 'DVR Madri'],
      ['dvrChimico', 'DVR Chimico'],
      ['dvrBiologico', 'DVR Biologico'],
      ['dvrROA', 'DVR ROA'],
      ['dvrConfinati', 'Amb. Confinati'],
      ['dvrVDT', 'Rischio VDT'],
      ['dvrAlcool', 'DVR Alcool'],
      ['dvrMMC', 'DVR MMC'],
      ['dvrRumore', 'DVR Rumore'],
      ['dvrVibrazioni', 'DVR Vibrazioni'],
      ['dvrAtex', 'ATEX polveri'],
    ],
  },
  {
    title: 'Documenti e piani',
    items: [
      ['duvri', 'DUVRI'],
      ['pei', 'PEI'],
      ['vrIncendio', 'VRI antincendio'],
      ['manualeHACCP', 'Manuale HACCP'],
      ['dvrPiscina', 'DVR Piscina'],
      ['dvrLegionella', 'DVR Legionella'],
      ['privacy', 'Privacy'],
    ],
  },
  {
    title: 'Presidi periodici',
    items: [
      ['sopralluoghiSicurezza', 'Sopralluoghi sicurezza (annui)'],
      ['sopralluoghiIgiene', 'Sopralluoghi igiene (annui)'],
      ['areaWeb', 'Area WEB'],
    ],
  },
];

const ServizioValue = ({ value }) => {
  if (value === undefined || value === null || value === '') {
    return <span className="text-slate-300 text-xs">—</span>;
  }
  const v = String(value).trim();
  if (v.toUpperCase() === 'SI' || v.toUpperCase() === 'SÌ') {
    return <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold">SI</span>;
  }
  if (v.toUpperCase() === 'NO') {
    return <span className="inline-block px-2 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-200 text-[10px] font-semibold">NO</span>;
  }
  // Testo libero (es. nome medico)
  return <span className="text-xs font-semibold text-slate-700" title={v}>{v.slice(0, 40)}{v.length > 40 ? '…' : ''}</span>;
};

const ContrattoServiziPanel = ({ company }) => {
  if (!company) return null;
  const contr = (company.contratto || '').trim();
  const contrMeta = CONTRATTO_LABEL[contr];
  const servizi = company.servizi || {};
  const hasAnyService = Object.values(servizi).some((v) => v !== undefined && v !== null && v !== '' && String(v).toUpperCase() !== 'NO');

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid="contratto-servizi-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-[#023F80] flex items-center gap-2">
            Contratto e servizi
          </h3>
          <p className="text-xs text-slate-500">Perimetro del contratto CSC e servizi acquistati dall'azienda</p>
        </div>
        {contrMeta ? (
          <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold ${contrMeta.color}`} data-testid="contratto-badge">
            <span className="font-mono text-[10px] opacity-70">{contr}</span>
            <span>{contrMeta.label}</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold bg-rose-50 text-rose-700 border-rose-200" data-testid="contratto-badge">
            <Info size={12}/> Contratto da verificare
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5 text-xs">
        <Field label="Numero contratto" value={company.contrattoNumero} />
        <Field label="Data contratto" value={company.contrattoData} />
        <Field label="Referente interno" value={company.referenteInterno} />
        <Field label="Medico competente" value={company.medicoCompetente} />
        <Field label="RSPP" value={company.rspp_csc} />
        <Field label="Aggiornato il" value={company.updatedAt ? String(company.updatedAt).slice(0, 10) : '—'} />
      </div>

      {!hasAnyService ? (
        <p className="text-xs text-slate-400 italic">Nessun servizio dettagliato censito per questa azienda. Puoi importare i valori dal file "ELENCO CONTRATTI E SERVIZI".</p>
      ) : (
        <div className="space-y-4">
          {SERVIZI_GROUPS.map((g) => (
            <div key={g.title}>
              <div className="text-[10px] uppercase font-semibold tracking-wide text-slate-500 mb-1.5">{g.title}</div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1.5">
                {g.items.map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between border-b border-slate-100 py-1">
                    <span className="text-xs text-slate-600">{label}</span>
                    <ServizioValue value={servizi[key]} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase text-slate-500 font-semibold">{label}</div>
    <div className="text-slate-800 font-medium">{value || <span className="text-slate-300">—</span>}</div>
  </div>
);

export default ContrattoServiziPanel;
